import { useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";

import FormFieldPassword from "../../input/FormFieldPassword";
import FormFieldText from "../../input/FormFieldText";
import useTriggeredAPICall from "../../../hooks/fetch-hooks/useTriggeredAPICall";
import { isValidEmail, isValidPassword } from "../../../util/stringUtils";
import { errorToast, successfulToast } from "../../../util/toastNotifications";

import Logo from "../../../assets/images/twlogo.svg";
import SubtlePrism from "../../../assets/images/subtle-prism.svg";

const INITIAL_FORM = {
  firstName: "",
  lastName: "",
  email: "",
  password: "",
};

const Register = ({ history }) => {
  const [formFields, setFormFields] = useState(INITIAL_FORM);
  const [formErrors, setFormErrors] = useState(false);

  const { fetchData, isLoading } = useTriggeredAPICall();

  const validateForm = () => {
    const errors = {};

    if (!formFields.firstName.trim()) {
      errors["firstName"] = "First Name is a required field";
    }

    if (!formFields.lastName.trim()) {
      errors["lastName"] = "Last Name is a required field";
    }

    if (!isValidEmail(formFields.email)) {
      errors["email"] = "Valid email is a required field";
    }

    if (!isValidPassword(formFields.password)) {
      errors["password"] =
        "* Password must be between 8-20 characters with one special character, one number, and one capital letter.";
    }

    return Object.keys(errors).length > 0 ? errors : null;
  };

  const handleFieldUpdate = (e) => {
    const { name, value } = e.target;

    setFormFields((prev) => ({ ...prev, [name]: value }));

    if (formErrors[name]) setFormErrors((prev) => ({ ...prev, [name]: null }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const formValidation = validateForm();

    if (formValidation) {
      setFormErrors(formValidation);
    } else {
      const payload = {
        email: formFields.email,
        password: formFields.password,
        first_name: formFields.firstName,
        last_name: formFields.lastName,
      };

      fetchData("/user/register", "POST", payload, null, false)
        .then(() => {
          successfulToast("Registration successful. Please check your email to verify your account.");
          history.push("/login/verify-account");
        })
        .catch(() => {
          errorToast("Registration failed. Please try again.");
        });
    }
  };

  return (
    <div
      className="width-full height-full flex-columns flex-align-center"
      style={{ backgroundImage: `url(${SubtlePrism})` }}
    >
      <Link to="/login">
        <img className="mt-32 mb-16 height-20" src={Logo} alt="Logo" />
      </Link>
      <div className="width-500px height-600px py-8 px-16 card">
        <h2>Create an Account</h2>
        <div>
          Already have an account?&nbsp;
          <Link className="no-decoration" to="/login">
            Login here!
          </Link>
        </div>
        <div className="error-message">{formErrors?.login}</div>
        <form onSubmit={handleSubmit}>
          <FormFieldText
            name="firstName"
            label="First Name"
            placeholder="Enter your first name..."
            value={formFields.firstName}
            onChange={handleFieldUpdate}
          ></FormFieldText>

          <FormFieldText
            name="lastName"
            label="Last Name"
            placeholder="Enter your last name..."
            value={formFields.lastName}
            onChange={handleFieldUpdate}
          ></FormFieldText>

          <FormFieldText
            name="email"
            label="Email"
            placeholder="Enter your email..."
            type="email"
            value={formFields.email}
            onChange={handleFieldUpdate}
          ></FormFieldText>

          <FormFieldPassword
            name="password"
            label="Password"
            placeholder="Enter your password..."
            value={formFields.password}
            onChange={handleFieldUpdate}
            errorMessage={formErrors.password}
          />

          <button
            className="create-btn"
            type="submit"
            disabled={!formFields.email || !formFields.password || isLoading}
          >
            {isLoading ? (
              <FontAwesomeIcon icon={faSpinner} />
            ) : (
              "Create Account"
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Register;
